/**
 * Admin Dashboard Hooks
 *
 * TanStack Query hooks for admin dashboard functionality
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// Query key factory
export const adminKeys = {
  all: ['admin'] as const,
  users: () => [...adminKeys.all, 'users'] as const,
  usersList: (filters: UserListFilters) => [...adminKeys.users(), filters] as const,
  user: (id: string) => [...adminKeys.users(), id] as const,
  subscriptions: () => [...adminKeys.all, 'subscriptions'] as const,
  auditLogs: () => [...adminKeys.all, 'audit-logs'] as const,
  systemHealth: () => [...adminKeys.all, 'system-health'] as const,
  healthHistory: (service: string) => [...adminKeys.systemHealth(), 'history', service] as const,
  availability: () => [...adminKeys.systemHealth(), 'availability'] as const,
  incidents: () => [...adminKeys.systemHealth(), 'incidents'] as const,
  analytics: () => [...adminKeys.all, 'analytics'] as const,
  subscriptionMetrics: (days: number) => [...adminKeys.analytics(), 'subscriptions', days] as const,
  mrrTrend: (days: number) => [...adminKeys.analytics(), 'mrr-trend', days] as const,
  churn: (days: number) => [...adminKeys.analytics(), 'churn', days] as const,
  ltv: () => [...adminKeys.analytics(), 'ltv'] as const,
  revenue: (periodType: string, periods: number) =>
    [...adminKeys.analytics(), 'revenue', periodType, periods] as const,
  impersonation: () => [...adminKeys.all, 'impersonation'] as const,
  activeImpersonations: () => [...adminKeys.impersonation(), 'active'] as const,
  impersonationHistory: () => [...adminKeys.impersonation(), 'history'] as const,
};

// Types
export interface UserListFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  emailVerified?: boolean;
  hasSubscription?: boolean;
  role?: 'admin' | 'super_admin' | 'support' | 'user';
  status?: 'active' | 'suspended' | 'banned';
}

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string;
  emailVerified: boolean;
  status: 'active' | 'suspended' | 'banned';
  suspendedAt?: string;
  suspendedUntil?: string;
  bannedAt?: string;
  createdAt: string;
  updatedAt: string;
  subscription?: {
    id: string;
    status: string;
    planId: string;
    currentPeriodEnd: string;
  } | null;
  adminRole?: {
    role: string;
    grantedAt: string;
  } | null;
}

export interface PaginatedUsers {
  users: AdminUser[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface ServiceHealth {
  service: string;
  status: 'healthy' | 'degraded' | 'down';
  responseTimeMs: number;
  lastChecked: string;
  details?: Record<string, unknown>;
  error?: string;
}

export interface SystemHealth {
  overall: 'healthy' | 'degraded' | 'down';
  uptime: number;
  timestamp: string;
  services: ServiceHealth[];
}

export interface SubscriptionMetrics {
  metrics: {
    mrr: number;
    mrrFormatted: string;
    activeSubscriptions: number;
    totalCustomers: number;
    churnRate: number;
    conversionRate: number;
    planDistribution: Record<string, number>;
    recentCancellations: number;
    newSubscriptions: number;
  };
  period: {
    days: number;
    start: string;
    end: string;
  };
}

export interface MRRTrendPoint {
  date: string;
  mrr: number;
  mrrFormatted: string;
  subscriptionCount: number;
}

export interface ChurnAnalysis {
  churn: {
    rate: number;
    churned: number;
    total: number;
    byPlan: Record<string, { churned: number; rate: number }>;
    reasons: Array<{ reason: string; count: number }>;
  };
  period: { days: number };
}

export interface LTVData {
  ltv: {
    average: number;
    averageFormatted: string;
    byPlan: Record<string, { value: number; formatted: string }>;
  };
}

export interface RevenueData {
  revenue: Array<{
    period: string;
    revenue: number;
    revenueFormatted: string;
    subscriptionCount: number;
  }>;
  periodType: string;
  periods: number;
}

export interface ImpersonationSession {
  id: string;
  targetUserId: string;
  reason: string;
  startedAt: string;
  expiresAt: string;
  endedAt?: string;
  admin?: {
    id: string;
    email: string;
    name: string;
  };
  targetUser?: {
    id: string;
    email: string;
    name: string;
  };
}

// ==================
// User Management
// ==================

export function useAdminUsers(filters: UserListFilters = {}) {
  const queryParams = new URLSearchParams();
  if (filters.page) queryParams.set('page', String(filters.page));
  if (filters.pageSize) queryParams.set('pageSize', String(filters.pageSize));
  if (filters.search) queryParams.set('search', filters.search);
  if (filters.emailVerified !== undefined)
    queryParams.set('emailVerified', String(filters.emailVerified));
  if (filters.hasSubscription !== undefined)
    queryParams.set('hasSubscription', String(filters.hasSubscription));
  if (filters.role) queryParams.set('role', filters.role);

  return useQuery({
    queryKey: adminKeys.usersList(filters),
    queryFn: () => api.get<PaginatedUsers>(`/admin/users?${queryParams.toString()}`),
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useAdminUser(userId: string) {
  return useQuery({
    queryKey: adminKeys.user(userId),
    queryFn: () => api.get<{ user: AdminUser }>(`/admin/users/${userId}`),
    enabled: !!userId,
  });
}

export function useSuspendUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      reason,
      durationHours,
    }: {
      userId: string;
      reason: string;
      durationHours?: number;
    }) => api.post(`/admin/users/${userId}/suspend`, { reason, durationHours }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export function useUnsuspendUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => api.post(`/admin/users/${userId}/unsuspend`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export function useBanUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason: string }) =>
      api.post(`/admin/users/${userId}/ban`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export function useUnbanUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => api.post(`/admin/users/${userId}/unban`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

// ==================
// Impersonation
// ==================

export function useStartImpersonation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      reason,
      durationMinutes,
    }: {
      userId: string;
      reason: string;
      durationMinutes?: number;
    }) => api.post<{ session: ImpersonationSession }>(`/admin/impersonate/${userId}`, { reason, durationMinutes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.activeImpersonations() });
    },
  });
}

export function useEndImpersonation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => api.post(`/admin/impersonate/${sessionId}/end`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.activeImpersonations() });
    },
  });
}

export function useActiveImpersonations() {
  return useQuery({
    queryKey: adminKeys.activeImpersonations(),
    queryFn: () => api.get<{ sessions: ImpersonationSession[] }>('/admin/impersonate/active'),
    refetchInterval: 30 * 1000, // Refresh every 30 seconds
  });
}

export function useImpersonationHistory(filters: { page?: number; pageSize?: number } = {}) {
  const queryParams = new URLSearchParams();
  if (filters.page) queryParams.set('page', String(filters.page));
  if (filters.pageSize) queryParams.set('pageSize', String(filters.pageSize));

  return useQuery({
    queryKey: adminKeys.impersonationHistory(),
    queryFn: () =>
      api.get<{
        sessions: ImpersonationSession[];
        pagination: { page: number; pageSize: number; total: number; totalPages: number };
      }>(`/admin/impersonate/history?${queryParams.toString()}`),
  });
}

// ==================
// System Health
// ==================

export function useSystemHealth() {
  return useQuery({
    queryKey: adminKeys.systemHealth(),
    queryFn: () => api.get<SystemHealth>('/admin/system/health'),
    refetchInterval: 60 * 1000, // Refresh every minute
    staleTime: 30 * 1000,
  });
}

export function useServiceHealthHistory(service: string, hours: number = 24) {
  return useQuery({
    queryKey: adminKeys.healthHistory(service),
    queryFn: () =>
      api.get<{
        service: string;
        period: { hours: number };
        snapshots: Array<{
          id: string;
          status: string;
          responseTimeMs: Record<string, number>;
          timestamp: string;
        }>;
      }>(`/admin/system/health/history?service=${service}&hours=${hours}`),
    enabled: !!service,
  });
}

export function useServicesAvailability() {
  return useQuery({
    queryKey: adminKeys.availability(),
    queryFn: () =>
      api.get<{
        period: string;
        services: Record<string, { availability: number; status: string }>;
      }>('/admin/system/availability'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useRecentIncidents() {
  return useQuery({
    queryKey: adminKeys.incidents(),
    queryFn: () =>
      api.get<{
        period: string;
        incidents: Array<{
          service: string;
          status: string;
          startTime: string;
          endTime?: string;
          duration?: number;
        }>;
      }>('/admin/system/incidents'),
    staleTime: 60 * 1000, // 1 minute
  });
}

// ==================
// Subscription Analytics
// ==================

export function useSubscriptionMetrics(periodDays: number = 30) {
  return useQuery({
    queryKey: adminKeys.subscriptionMetrics(periodDays),
    queryFn: () => api.get<SubscriptionMetrics>(`/admin/analytics/subscriptions?periodDays=${periodDays}`),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useMRRTrend(periodDays: number = 30) {
  return useQuery({
    queryKey: adminKeys.mrrTrend(periodDays),
    queryFn: () =>
      api.get<{ trend: MRRTrendPoint[]; period: { days: number } }>(
        `/admin/analytics/mrr-trend?periodDays=${periodDays}`
      ),
    staleTime: 5 * 60 * 1000,
  });
}

export function useChurnAnalysis(periodDays: number = 30) {
  return useQuery({
    queryKey: adminKeys.churn(periodDays),
    queryFn: () => api.get<ChurnAnalysis>(`/admin/analytics/churn?periodDays=${periodDays}`),
    staleTime: 5 * 60 * 1000,
  });
}

export function useLTV() {
  return useQuery({
    queryKey: adminKeys.ltv(),
    queryFn: () => api.get<LTVData>('/admin/analytics/ltv'),
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
}

export function useRevenueByPeriod(periodType: 'day' | 'week' | 'month' = 'month', periods: number = 12) {
  return useQuery({
    queryKey: adminKeys.revenue(periodType, periods),
    queryFn: () =>
      api.get<RevenueData>(`/admin/analytics/revenue?periodType=${periodType}&periods=${periods}`),
    staleTime: 5 * 60 * 1000,
  });
}
